import { Component, OnInit, AfterViewChecked, ElementRef, ViewChildren, QueryList } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SuiteService } from '../../Services/suite.service';
import { SharedService } from '../../Services/shared.service';
import { AuthenticationService } from '../../Services/authentication.service';
import { ServiceEndpointsService } from 'src/app/Services/service-endpoints.service';
import { NotifyService } from 'src/app/Services/notify.service';
import { AddElementComponent } from '../../AppBaseComponents/add-element/add-element.component';
import { TopNavComponentComponent } from '../../AppBaseComponents/top-nav-component/top-nav-component.component';
import { SuiteConfigSelectionDialogComponent } from '../suite-config-selection-dialog/suite-config-selection-dialog.component';
import { SuiteExecutionConfigDialogComponent, ExecutionConfig } from '../suite-execution-config-dialog/suite-execution-config-dialog.component';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-ci-cd',
  templateUrl: './ci-cd.component.html',
  styleUrls: ['./ci-cd.component.scss']
})
export class CiCdComponent implements OnInit, AfterViewChecked {
  @ViewChildren('suiteName') suiteNameElements!: QueryList<ElementRef>;
  
  public suites: any[] = [];
  public selectedSuites: Set<string> = new Set();
  public isLoading = false;
  public errorMsg = '';
  public overflowingItems: Set<string> = new Set();
  public suiteConfigurations: Map<string, ExecutionConfig | 'default'> = new Map();

  constructor(
    private suiteService: SuiteService,
    private sharedService: SharedService,
    private dialog: MatDialog,
    private authenticationService: AuthenticationService,
    private notifys: NotifyService,
    private serviceEndpointsService: ServiceEndpointsService,
  ) {}

  ngOnInit() {
    TopNavComponentComponent.currentPage = { name: 'CI/CD' };
    this.loadSuites();
  }

  ngAfterViewChecked() {
    this.checkOverflow();
  }

  checkOverflow() {
    if (this.suiteNameElements) {
      this.suiteNameElements.forEach((element, index) => {
        const span = element.nativeElement;
        const parent = span.parentElement;
        
        if (span && parent) {
          const isOverflowing = span.scrollWidth > parent.clientWidth;
          const suiteId = this.suites[index]._id || this.suites[index].id || this.suites[index].name;
          
          if (isOverflowing) {
            this.overflowingItems.add(suiteId);
          } else {
            this.overflowingItems.delete(suiteId);
          }
        }
      });
    }
  }

  isOverflowing(suite: any): boolean {
    const id = suite._id || suite.id || suite.name;
    return this.overflowingItems.has(id);
  }

  loadSuites() {
    this.isLoading = true;
    this.sharedService.loaderHide.emit(true);
    this.suiteService.getItems().subscribe({
      next: (response: any) => {
        this.isLoading = false;
        this.sharedService.loaderHide.emit(false);
        this.suites = response.data || response || [];
      },
      error: (err) => {
        this.isLoading = false;
        this.sharedService.loaderHide.emit(false);
        console.error('Error loading suites:', err);
        this.errorMsg = 'Failed to load suites. Please try again.';
      }
    });
  }

  toggleSelection(suite: any) {
    const id = suite._id || suite.id || suite.name;
    if (!this.selectedSuites.has(id)) {
      this.showConfigurationDialog(suite, false);
    }
  }

  editSuiteConfiguration(suite: any, event: Event) {
    event.stopPropagation();
    this.showConfigurationDialog(suite, true);
  }

  deselectSuite(suite: any, event: Event) {
    event.stopPropagation();
    const id = suite._id || suite.id || suite.name;
    this.selectedSuites.delete(id);
    this.suiteConfigurations.delete(id);
    this.notifys.notify('info', 'Suite Removed', `${suite.name || 'Suite'} removed from selection`);
  }

  showConfigurationDialog(suite: any, isEdit: boolean = false) {
    const suiteId = suite._id || suite.id || suite.name;
    const suiteName = suite.name || 'Unknown Suite';
    const existingConfig = this.suiteConfigurations.get(suiteId);

    if (isEdit && existingConfig && existingConfig !== 'default') {
      const configData = {
        suiteName: suiteName,
        suiteId: suiteId,
        existingConfig: existingConfig
      };

      const configDialog = this.dialog.open(SuiteExecutionConfigDialogComponent, {
        data: configData,
        width: '39vw',
        maxHeight: '90vh',
        panelClass: 'custom-dialog-container'
      });

      configDialog.afterClosed().subscribe((config: ExecutionConfig | null) => {
        if (config) {
          this.suiteConfigurations.set(suiteId, config);
          this.notifys.notify('success', 'Configuration Updated', `Configuration updated for ${suiteName}`);
        }
      });
      return;
    }

    const configSelectionDialog = this.dialog.open(SuiteConfigSelectionDialogComponent, {
      data: { suiteName: suiteName },
      width: '39vw',
      panelClass: 'custom-dialog-container'
    });

    configSelectionDialog.afterClosed().subscribe((configType: string | null) => {
      if (configType === null) {
        return;
      }

      if (configType === 'default') {
        this.selectedSuites.add(suiteId);
        this.suiteConfigurations.set(suiteId, 'default');
        if (isEdit) {
          this.notifys.notify('success', 'Configuration Updated', `${suiteName} updated to default configuration`);
        } else {
          this.notifys.notify('success', 'Suite Added', `${suiteName} added with default configuration`);
        }
      } else if (configType === 'custom') {
        const configData = {
          suiteName: suiteName,
          suiteId: suiteId,
          existingConfig: (existingConfig !== 'default' && existingConfig) ? existingConfig : undefined
        };

        const configDialog = this.dialog.open(SuiteExecutionConfigDialogComponent, {
          data: configData,
          width: '39vw',
          maxHeight: '90vh',
          panelClass: 'custom-dialog-container'
        });

        configDialog.afterClosed().subscribe((config: ExecutionConfig | null) => {
          if (config) {
            this.selectedSuites.add(suiteId);
            this.suiteConfigurations.set(suiteId, config);
            if (isEdit) {
              this.notifys.notify('success', 'Configuration Updated', `Configuration updated for ${suiteName}`);
            } else {
              this.notifys.notify('success', 'Configuration Saved', `Custom configuration saved for ${suiteName}`);
            }
          }
        });
      }
    });
  }

  getDefaultConfiguration(): ExecutionConfig {
    return {
      browser: ['Chrome'],
      hub_ip: environment.selenoidHubIp,
      port: environment.selenoidHubPort || 4445,
      default_pause: '0',
      send_mail: false,
      resolution: '1920x1200',
      screenshot_required: false,
      default_hub: true,
      local_execution: false,
      execution_machine: 'Default Machine'
    };
  }

  getConfigurationForSuite(suiteId: string): ExecutionConfig {
    const config = this.suiteConfigurations.get(suiteId);
    if (config === 'default' || !config) {
      return this.getDefaultConfiguration();
    }
    return config;
  }

  getComputedResolution(config: ExecutionConfig): string {
    if (config.resolution === 'default_resolution') {
      return '1920x1200';
    } else if (config.resolution === 'custom_resolution') {
      const x = config.xDimension || 1920;
      const y = config.yDimension || 1200;
      return `${x}x${y}`;
    }
    return config.resolution;
  }

  hasCustomConfig(suite: any): boolean {
    const id = suite._id || suite.id || suite.name;
    const config = this.suiteConfigurations.get(id);
    return config !== undefined && config !== 'default';
  }

  isSelected(suite: any): boolean {
    const id = suite._id || suite.id || suite.name;
    return this.selectedSuites.has(id);
  }

  resetSelection() {
    this.selectedSuites.clear();
    this.overflowingItems.clear();
    this.suiteConfigurations.clear();
  }

  getSelectedSuites() {
    return this.suites.filter(s =>
      this.selectedSuites.has(s._id || s.id || s.name)
    );
  }

  createEnvFileForSelectedSuites() {
    const selectedSuitesArray = this.getSelectedSuites();
    
    if (selectedSuitesArray.length === 0) {
      this.notifys.notify('warning', 'No suites selected', 'Please select at least one suite');
      return;
    }

    console.log("Selected suite IDs:", selectedSuitesArray.map(s => s._id));

    // First, get service endpoints
    this.serviceEndpointsService.getServiceEndpoints().subscribe(
      (endpointsResponse) => {
        let backendUrl = endpointsResponse.data ? endpointsResponse.data.backendUrl : null;
        
        if (!backendUrl) {
          backendUrl = environment.backendEndpoint + ':8080';
        }
        console.log("Backend URL retrieved:", backendUrl);

        // Now proceed with password dialog
        let dialogRef = this.dialog.open(AddElementComponent, {
          data: {
            event: 'Pwd',
            title: 'Enter password:',
            name: ''
          }
        });

        dialogRef.afterClosed().subscribe(result => {
          if (!result || !result.name) {
            this.notifys.notify('warning', 'No password entered', 'Operation cancelled');
            return;
          }

          const enteredPassword = result.name;
          const currentUsername = localStorage.getItem('username');

          this.authenticationService.login(currentUsername, enteredPassword).subscribe(
            () => {
              // Get array of suite IDs with their configurations
              const suiteConfigs: Array<{ id: string, config: ExecutionConfig }> = selectedSuitesArray.map(suite => {
                const suiteId = suite._id || suite.id || suite.name;
                return {
                  id: suiteId,
                  config: this.getConfigurationForSuite(suiteId)
                };
              });

              // Generate suite configurations as an array of JSON objects
              const suiteConfigsArray = suiteConfigs.map((item) => {
                const config = item.config;
                const resolution = this.getComputedResolution(config);
                let port;
                if (config.isURLWithoutPort) {
                  port = -1;
                } else if (config.port === '' || config.port === null || config.port === undefined) {
                  port = '';
                } else {
                  port = config.port;
                }
                const hubIp = config.hub_ip || environment.selenoidHubIp;
                const executionMachine = config.execution_machine || 'Default Machine';
                
                const executionConfig = {
                  suite_id: item.id,
                  browser: config.browser.map(b => b),
                  hub_ip: hubIp,
                  port: port,
                  send_mail: config.send_mail,
                  resolution: resolution,
                  default_pause: config.default_pause || '0',
                  is_implicit_default_pause: !!config.is_implicit_default_pause,
                  screenshot_required: config.screenshot_required,
                  default_hub: config.default_hub,
                  local_execution: config.local_execution,
                  execution_machine: executionMachine
                };

                const suiteResourceConfig = {
                  use_csv: true,
                  update_csv: false,
                  use_apk: false,
                  update_apk: false,
                  use_ipa: false,
                  update_ipa: false
                };

                const payload = {
                  project: { _id: localStorage.getItem('projectId') },
                  execution: executionConfig,
                  suite_resource_config: suiteResourceConfig
                };

                let jsonString = JSON.stringify(payload);
                jsonString = jsonString.replace(/\\/g, '\\\\').replace(/\$/g, '\\$').replace(/`/g, '\\`');
                
                return {
                  suiteId: item.id,
                  payloadJson: jsonString
                };
              });

              // Generate suite data declarations using heredoc
              const suiteDataDeclarations = suiteConfigsArray.map((item, index) => {
                const suiteName = selectedSuitesArray[index].name || item.suiteId;
                return `# Suite ${index}: ${item.suiteId}
read -r -d '' SUITE_${index} << 'EOF'
${item.payloadJson}
EOF

SUITE_IDS[${index}]="${item.suiteId}"
SUITE_NAMES[${index}]="${suiteName}"`;
              }).join('\n\n');

              const escapedPassword = result.name
                .replace(/\\/g, '\\\\')
                .replace(/"/g, '\\"')
                .replace(/\$/g, '\\\$')
                .replace(/`/g, '\\`');

              const scriptContent = `#!/bin/bash
set -e  # Exit script on first error
set -o pipefail  # Fail if any command in a pipeline fails

# Define environment variables
API_BASE_URL="${backendUrl}"
API_USERNAME="${localStorage.getItem('username')}"
API_PASSWORD="${escapedPassword}"
PROJECT_ID="${localStorage.getItem('projectId')}"

# Arrays to store suite IDs and names
declare -a SUITE_IDS
declare -a SUITE_NAMES

# Suite configurations defined using heredoc
# Temporarily disable exit on error for heredoc reads
set +e
${suiteDataDeclarations}
set -e

SUITE_COUNT=${suiteConfigsArray.length}

# -------------------------------------------------------
# Step 1: Authenticate
# -------------------------------------------------------
RESPONSE=\$(curl -s -X POST "\$API_BASE_URL/authenticate" \\
  -H "Content-Type: application/json" \\
  -d '{"username": "'"\$API_USERNAME"'", "password": "'"\$API_PASSWORD"'", "loginType": "EXTENSION"}')

TOKEN=\$(echo "\$RESPONSE" | jq -r '.token')

if [[ "\$TOKEN" == "null" || -z "\$TOKEN" ]]; then
    echo "Authentication failed!"
    exit 1
fi

echo "Authentication successfull!"

# -------------------------------------------------------
# Step 2: Trigger all suite executions and collect execution IDs
# -------------------------------------------------------

# Array to store execution IDs returned after triggering each suite
declare -a EXECUTION_IDS

# Temp files to store name mapping and final statuses — works on all bash versions
NAME_FILE=\$(mktemp /tmp/xpeer_names_XXXXXX)
STATUS_FILE=\$(mktemp /tmp/xpeer_status_XXXXXX)
trap 'rm -f "\$NAME_FILE" "\$STATUS_FILE"' EXIT

echo ""
echo "Triggering \${SUITE_COUNT} suite(s)..."

for i in \$(seq 0 \$((SUITE_COUNT - 1))); do
  SUITE_VAR="SUITE_\$i"
  SUITE_PAYLOAD="\${!SUITE_VAR}"
  SUITE_ID="\${SUITE_IDS[\$i]}"
  SUITE_NAME="\${SUITE_NAMES[\$i]}"

  # Validate JSON format before sending
  echo "\$SUITE_PAYLOAD" | jq . > /dev/null 2>&1 || { echo "  [!] Invalid JSON for suite \$SUITE_ID, skipping."; continue; }

  # Send execution request
  if [ -f optional_file.zip ]; then
    RESPONSE_DATA=\$(curl -s -w "\\n%{http_code}" -X POST "\$API_BASE_URL/execute/executeSuite" \\
      -H "Authorization: Bearer \$TOKEN" \\
      -H "Content-Type: multipart/form-data" \\
      -F "req=\$SUITE_PAYLOAD" \\
      -F "file=@optional_file.zip")
  else
    RESPONSE_DATA=\$(curl -s -w "\\n%{http_code}" -X POST "\$API_BASE_URL/execute/executeSuite" \\
      -H "Authorization: Bearer \$TOKEN" \\
      -H "Content-Type: multipart/form-data" \\
      -F "req=\$SUITE_PAYLOAD" \\
      -F "file=@/dev/null")
  fi

  HTTP_RESPONSE=\$(echo "\$RESPONSE_DATA" | tail -n1)
  RESPONSE_BODY=\$(echo "\$RESPONSE_DATA" | sed '\$d')

  if [[ "\$HTTP_RESPONSE" -ne 200 ]]; then
    echo "  [!] Suite \$SUITE_ID failed (HTTP \$HTTP_RESPONSE)"
  else
    EXEC_ID=\$(echo "\$RESPONSE_BODY" | jq -r 'if (.data | type) == "string" then .data else (.data.executionId // .executionId // empty) end')

    if [[ -n "\$EXEC_ID" && "\$EXEC_ID" != "null" ]]; then
      EXECUTION_IDS+=("\$EXEC_ID")
      echo "\$EXEC_ID=\$SUITE_NAME" >> "\$NAME_FILE"
      echo "  [+] \$SUITE_NAME "
    else
      echo "  [!] \$SUITE_NAME triggered but no execution ID returned"
    fi
  fi

  sleep 2
done

echo "  All suites triggered.  \${#EXECUTION_IDS[@]}"

# -------------------------------------------------------
# Step 3: Poll execution statuses every 30 seconds
# -------------------------------------------------------

# Terminal status patterns (case-insensitive match)
is_terminal_status() {
  local status_lower
  status_lower=\$(echo "\$1" | tr '[:upper:]' '[:lower:]')
  case "\$status_lower" in
    "execution successfull" | \
    "execution not performed please contact team before retrying" | \
    "execution failed")
      return 0 ;;
    *)
      return 1 ;;
  esac
}

declare -a ACTIVE_IDS=("\${EXECUTION_IDS[@]}")

if [[ \${#ACTIVE_IDS[@]} -eq 0 ]]; then
  echo "No execution IDs to poll. Script complete."
  exit 0
fi

POLL_INTERVAL=30
POLL_ROUND=0

echo ""
while [[ \${#ACTIVE_IDS[@]} -gt 0 ]]; do
  POLL_ROUND=\$((POLL_ROUND + 1))

  # Countdown timer
  printf "Checking Execution Status Round %d — next check in: " "\$POLL_ROUND"
  for t in \$(seq \$POLL_INTERVAL -10 10); do
    printf "%ds " "\$t"
    sleep 10
  done
  printf "\\n"

  # Build JSON payload
  IDS_JSON=\$(printf '%s\n' "\${ACTIVE_IDS[@]}" | jq -R . | jq -s '{executionIds: .}')

  STATUS_RESPONSE=\$(curl -s -X POST "\$API_BASE_URL/execute/getExecutionStatus" \\
    -H "Authorization: Bearer \$TOKEN" \\
    -H "Content-Type: application/json" \\
    -d "\$IDS_JSON")

  OP_STATUS=\$(echo "\$STATUS_RESPONSE" | jq -r '.operationStatus // empty')
  if [[ "\$OP_STATUS" != "SUCCESS" ]]; then
    echo "  [!] Status API error (\$OP_STATUS), retrying next round..."
    continue
  fi

  declare -a REMAINING_IDS=()

  for EXEC_ID in "\${ACTIVE_IDS[@]}"; do
    EXEC_STATUS=\$(echo "\$STATUS_RESPONSE" | jq -r --arg id "\$EXEC_ID" '.data[\$id] // empty')

    if [[ -z "\$EXEC_STATUS" ]]; then
      REMAINING_IDS+=("\$EXEC_ID")
      continue
    fi

    if is_terminal_status "\$EXEC_STATUS"; then
      # Write "execId=status" to temp file
      echo "\$EXEC_ID=\$EXEC_STATUS" >> "\$STATUS_FILE"
    else
      REMAINING_IDS+=("\$EXEC_ID")
    fi
  done

  ACTIVE_IDS=("\${REMAINING_IDS[@]}")
done

echo ""
echo "================================================"
echo "Fetching Execution Details"
echo "================================================"

# Build payload with all execution IDs
DETAILS_PAYLOAD=\$(printf '%s\n' "\${EXECUTION_IDS[@]}" | jq -R . | jq -s '{executionIds: .}')

DETAILS_RESPONSE=\$(curl -s -X POST "\$API_BASE_URL/execute/getExecutionDetails" \\
  -H "Authorization: Bearer \$TOKEN" \\
  -H "Content-Type: application/json" \\
  -d "\$DETAILS_PAYLOAD")

DETAILS_OP_STATUS=\$(echo "\$DETAILS_RESPONSE" | jq -r '.operationStatus // empty')

if [[ "\$DETAILS_OP_STATUS" != "SUCCESS" ]]; then
  echo "  [!] Could not fetch execution details (\$DETAILS_OP_STATUS), skipping details display."
fi

echo ""
echo "================================================"
echo "Execution Summary"
echo "================================================"
OVERALL_STATUS="PASSED"
for EXEC_ID in "\${EXECUTION_IDS[@]}"; do
  SUITE_NAME=\$(grep "^\$EXEC_ID=" "\$NAME_FILE" | head -n1 | cut -d'=' -f2-)
  FINAL_STATUS=\$(grep "^\$EXEC_ID=" "\$STATUS_FILE" | head -n1 | cut -d'=' -f2-)
  echo ""
  echo "Suite  : \${SUITE_NAME:-\$EXEC_ID}"
  echo "Status : \${FINAL_STATUS:-Unknown}"

  # If any execution is not successfull, mark overall as FAILED
  FINAL_STATUS_LOWER=\$(echo "\$FINAL_STATUS" | tr '[:upper:]' '[:lower:]')
  if [[ "\$FINAL_STATUS_LOWER" != "execution successfull" ]]; then
    OVERALL_STATUS="FAILED"
  fi

  # Print execution details if available
  if [[ "\$DETAILS_OP_STATUS" == "SUCCESS" ]]; then
    EXEC_OBJ=\$(echo "\$DETAILS_RESPONSE" | jq -c --arg id "\$EXEC_ID" '.data[\$id] // empty')

    if [[ -n "\$EXEC_OBJ" && "\$EXEC_OBJ" != "null" && "\$EXEC_OBJ" != "{}" && "\$EXEC_OBJ" != "[]" ]]; then

      # Detect structure — new format is object { reportShareLink, details[] }
      #                     old format is a flat array [ {browser, iteration, ...} ]
      IS_ARRAY=\$(echo "\$EXEC_OBJ" | jq -r 'if type == "array" then "yes" else "no" end')

      if [[ "\$IS_ARRAY" == "yes" ]]; then
        # Old structure — flat array, no reportShareLink
        REPORT_LINK=""
        DETAILS_JSON="\$EXEC_OBJ"
      else
        # New structure — object with reportShareLink + details[]
        REPORT_LINK=\$(echo "\$EXEC_OBJ" | jq -r '.reportShareLink // ""')
        DETAILS_JSON=\$(echo "\$EXEC_OBJ" | jq -c '.details // []')
      fi

      if [[ -n "\$REPORT_LINK" ]]; then
        echo "Report : \$REPORT_LINK"
      fi

      ITERATION_COUNT=\$(echo "\$DETAILS_JSON" | jq 'length')

      for i in \$(seq 0 \$((ITERATION_COUNT - 1))); do
        DETAIL=\$(echo "\$DETAILS_JSON" | jq -c ".[\$i]")
        BROWSER=\$(echo "\$DETAIL" | jq -r '.browser // "N/A"')
        ITERATION=\$(echo "\$DETAIL" | jq -r '.iteration // "N/A"')
        VIDEO_URL=\$(echo "\$DETAIL" | jq -r '.presignedVideoUrl // ""')
        FAILED_STEP=\$(echo "\$DETAIL" | jq -r '.stepName // ""')
        FAILED_COMMAND_STEP=\$(echo "\$DETAIL" | jq -r '.commandStep // ""')

        echo "  ┌─ Iteration \$ITERATION | Browser: \$BROWSER"

        if [[ -n "\$FAILED_STEP" ]]; then
          echo "  │  *** FAILED STEP: \$FAILED_STEP ***"
        fi

        if [[ -n "\$FAILED_COMMAND_STEP" ]]; then
          echo "  │  *** FAILED COMMAND STEP: \$FAILED_COMMAND_STEP ***"
        fi

        if [[ -n "\$VIDEO_URL" ]]; then
          echo "  │  Video  : \$VIDEO_URL"
        fi

        echo "  └─────────────────────────────────────"
      done
    fi
  fi
done
echo ""
echo "================================================"
echo "OVERALL EXECUTION STATUS: \$OVERALL_STATUS"
echo "================================================"`;

              const fileName = 'Xpeer.sh';
              const blob = new Blob([scriptContent], { type: 'text/plain' });
              const anchor = document.createElement('a');
              anchor.href = window.URL.createObjectURL(blob);
              anchor.download = fileName;
              anchor.click();

              this.notifys.notify('success', 'Script Generated', `Script for ${suiteConfigsArray.length} suite(s) downloaded successfully`);
              console.log('Script file generated for suites:', suiteConfigsArray.map(s => s.suiteId));
              this.selectedSuites.clear();
              this.overflowingItems.clear();
              this.suiteConfigurations.clear();
            },
            (err) => {
              this.notifys.notify('error', 'Incorrect password', 'The entered password does not match your account');
              console.error('Password validation failed', err);
            }
          );
        });
      },
      (error) => {
        this.notifys.notify('error', 'Service Error', 'Failed to retrieve service endpoints');
        console.error('Failed to get service endpoints', error);
      }
    );
  }
}